/**
 * Default zoom level used by the map UI and bounds computation.
 * Single source of truth — imported by useMap, miniMap, and the search loader.
 */
export const DEFAULT_MAP_ZOOM = 12;

interface LatLng {
  lat: number;
  lng: number;
}

interface Bounds {
  swLat: number;
  swLng: number;
  neLat: number;
  neLng: number;
}

/**
 * Computes geographic bounds from a center point and zoom level
 * using Web Mercator tile math.
 */
export function boundsFromCenter(center: LatLng, zoom: number): Bounds {
  // At a given zoom level, the world is divided into 2^zoom tiles of 256px.
  // We approximate the visible area as 512px × 512px (one tile in each direction).
  const tileCount = Math.pow(2, zoom);

  // Longitude degrees per pixel at this zoom
  const lngDegreesPerPixel = 360 / (tileCount * 256);
  // Half-width in degrees for a ~512px viewport
  const halfLngSpan = lngDegreesPerPixel * 256;

  // Latitude uses Mercator projection — convert to radians, project, offset, unproject
  const latRad = (center.lat * Math.PI) / 180;
  const mercY = Math.log(Math.tan(Math.PI / 4 + latRad / 2));

  const mercatorUnitsPerPixel = (2 * Math.PI) / (tileCount * 256);
  const halfLatSpanMercator = mercatorUnitsPerPixel * 256;

  const swMercY = mercY - halfLatSpanMercator;
  const neMercY = mercY + halfLatSpanMercator;

  const swLat = ((2 * Math.atan(Math.exp(swMercY)) - Math.PI / 2) * 180) / Math.PI;
  const neLat = ((2 * Math.atan(Math.exp(neMercY)) - Math.PI / 2) * 180) / Math.PI;

  return {
    swLat,
    swLng: center.lng - halfLngSpan,
    neLat,
    neLng: center.lng + halfLngSpan,
  };
}
